import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Flame, Home, Target } from 'lucide-react';
import { formatMoney, type PlaceInput as Place } from '@tcrd/shared';
import { Button, Card } from '@tcrd/ui';
import { PlaceInput } from '../../components/PlaceInput';
import { useToast } from '../../components/toast';
import { api, errorMessage } from '../../lib/api';
import { fmtTime } from '../../lib/format';

interface Goal {
  goal: string | null;
  earned: string;
  rides: number;
  percent: number | null;
  daysLeft: number;
}

interface DestinationMode {
  active: boolean;
  address: string | null;
  expiresAt: string | null;
  usesLeft: number;
}

/** Meta de ganancias de la semana con su progreso. */
export function WeeklyGoalCard({ refetchInterval }: { refetchInterval?: number }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['driver-goal'], queryFn: () => api<Goal>('/drivers/me/goal'), refetchInterval });
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState('');
  const g = q.data;

  const save = async () => {
    try {
      qc.setQueryData(['driver-goal'], await api('/drivers/me/goal', { method: 'PUT', body: { amount: amount ? Number(amount) : null } }));
      setEditing(false);
    } catch (e) {
      toast(errorMessage(e), 'danger');
    }
  };

  return (
    <Card className="p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold flex items-center gap-2"><Target className="size-5 text-brand-red" aria-hidden /> Meta de la semana</p>
        {!editing && (
          <button className="tap text-sm font-medium text-brand-red" onClick={() => (setAmount(g?.goal ? String(Math.round(Number(g.goal))) : ''), setEditing(true))}>
            {g?.goal ? 'Cambiar' : 'Fijar meta'}
          </button>
        )}
      </div>
      {editing ? (
        <div className="flex gap-2">
          <input
            type="number"
            inputMode="numeric"
            min={0}
            placeholder="Ej.: 15000"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="flex-1 h-11 rounded-xl border border-[var(--border)] bg-[var(--surface)] px-3"
            aria-label="Meta en pesos"
            autoFocus
          />
          <Button size="sm" onClick={() => void save()}>Guardar</Button>
        </div>
      ) : g?.goal && g.percent !== null ? (
        <>
          <div className="h-3 rounded-full bg-[var(--surface-2)] overflow-hidden" role="progressbar" aria-valuenow={g.percent} aria-valuemin={0} aria-valuemax={100}>
            <div className="h-full rounded-full bg-brand-red transition-[width]" style={{ width: `${g.percent}%` }} />
          </div>
          <p className="text-sm">
            <strong>{formatMoney(g.earned)}</strong> de {formatMoney(g.goal)} · {g.percent}%
            <span className="text-[var(--text-muted)]">
              {' '}
              · {g.rides} viaje{g.rides === 1 ? '' : 's'} · {g.percent >= 100 ? '¡Meta cumplida! 🎉' : `quedan ${g.daysLeft} día${g.daysLeft === 1 ? '' : 's'}`}
            </span>
          </p>
        </>
      ) : (
        <p className="text-sm text-[var(--text-muted)]">Esta semana llevas {formatMoney(g?.earned ?? '0')}. Fija una meta para ver tu progreso.</p>
      )}
    </Card>
  );
}

/** Modo destino: recibir solo avisos de solicitudes que van hacia un lugar (por ejemplo, la casa). */
export function DestinationModeCard() {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['driver-destination'], queryFn: () => api<DestinationMode>('/drivers/me/destination'), refetchInterval: 60_000 });
  const [picking, setPicking] = useState(false);
  const [place, setPlace] = useState<Place | null>(null);
  const d = q.data;

  const set = async (p: Place) => {
    try {
      qc.setQueryData(['driver-destination'], await api('/drivers/me/destination', { method: 'PUT', body: { address: p.address, lat: p.lat, lng: p.lng } }));
      setPicking(false);
      setPlace(null);
      toast('Modo destino activo', 'success');
    } catch (e) {
      toast(errorMessage(e), 'danger');
    }
  };
  const clear = async () => {
    try {
      qc.setQueryData(['driver-destination'], await api('/drivers/me/destination', { method: 'DELETE' }));
    } catch (e) {
      toast(errorMessage(e), 'danger');
    }
  };

  return (
    <Card className="p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold flex items-center gap-2"><Home className="size-5 text-[#2563eb]" aria-hidden /> Modo destino</p>
        <Link to="/driver/demand" className="tap text-sm font-medium text-brand-red flex items-center gap-1"><Flame className="size-4" aria-hidden /> Zonas con demanda</Link>
      </div>
      {d?.active ? (
        <>
          <p className="text-sm">
            Solo te avisamos de viajes que van hacia <strong>{d.address}</strong>
            {d.expiresAt ? ` (hasta las ${fmtTime(new Date(d.expiresAt))})` : ''}. Tus propios clientes te siguen llegando siempre.
          </p>
          <Button size="sm" variant="secondary" onClick={() => void clear()}>Desactivar</Button>
        </>
      ) : picking ? (
        <>
          <PlaceInput label="¿Hacia dónde vas?" value={place} onChange={(p) => (setPlace(p), p && void set(p))} dotClass="bg-[#2563eb]" />
          <Button size="sm" variant="ghost" onClick={() => setPicking(false)}>Cancelar</Button>
        </>
      ) : (
        <>
          <p className="text-sm text-[var(--text-muted)]">¿Terminas el día? Recibe solo solicitudes que te acerquen a casa. Te quedan {d?.usesLeft ?? 2} usos hoy.</p>
          <Button size="sm" variant="secondary" disabled={!d || d.usesLeft === 0} onClick={() => setPicking(true)}>Elegir destino</Button>
        </>
      )}
    </Card>
  );
}
